import { useId, type ChangeEvent, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import styles from './Field.module.css'

type Size = 'sm' | 'md'

interface WrapProps {
  id: string
  label?: ReactNode
  required?: boolean
  error?: ReactNode
  size: Size
  children: ReactNode
}

function Wrap({ id, label, required, error, size, children }: WrapProps) {
  return (
    <div className={[styles.field, size === 'sm' && styles.sm].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={id} className={[styles.label, required && styles.required].filter(Boolean).join(' ')}>
          {label}
        </label>
      )}
      {children}
      {error && <span className={styles.error}>{error}</span>}
    </div>
  )
}

/* ── Text / password ─────────────────────────────────────────────── */

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value'> {
  label?: ReactNode
  value: string
  onChange: (value: string) => void
  size?: Size
  error?: ReactNode
  type?: 'text' | 'password' | 'email'
}

export function TextField({ label, value, onChange, size = 'md', error, required, id: givenId, type = 'text', ...rest }: TextFieldProps) {
  const auto = useId()
  const id = givenId ?? auto
  return (
    <Wrap id={id} label={label} required={required} error={error} size={size}>
      <input
        id={id}
        type={type}
        className={styles.control}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
    </Wrap>
  )
}

/* ── Textarea ────────────────────────────────────────────────────── */

export interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> {
  label?: ReactNode
  value: string
  onChange: (value: string) => void
  size?: Size
  error?: ReactNode
}

export function TextAreaField({ label, value, onChange, size = 'md', error, required, id: givenId, rows = 3, ...rest }: TextAreaFieldProps) {
  const auto = useId()
  const id = givenId ?? auto
  return (
    <Wrap id={id} label={label} required={required} error={error} size={size}>
      <textarea
        id={id}
        className={`${styles.control} ${styles.textarea}`}
        value={value}
        rows={rows}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
    </Wrap>
  )
}

/* ── Number ──────────────────────────────────────────────────────── */

export interface NumberFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value' | 'type'> {
  label?: ReactNode
  /* Empty string while the user has cleared the box. */
  value: number | ''
  onChange: (value: number | '') => void
  size?: Size
  error?: ReactNode
}

export function NumberField({ label, value, onChange, size = 'md', error, required, id: givenId, ...rest }: NumberFieldProps) {
  const auto = useId()
  const id = givenId ?? auto
  function handle(e: ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value
    if (raw === '') return onChange('')
    const n = Number(raw)
    if (!Number.isNaN(n)) onChange(n)
  }
  return (
    <Wrap id={id} label={label} required={required} error={error} size={size}>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        className={styles.control}
        value={value}
        required={required}
        onChange={handle}
        {...rest}
      />
    </Wrap>
  )
}

/* ── Select ──────────────────────────────────────────────────────── */

export interface SelectOption {
  value: string
  label: string
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'onChange' | 'value'> {
  label?: ReactNode
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  size?: Size
  error?: ReactNode
  /* No box: reads as a text action with an arrow. */
  bare?: boolean
}

export function SelectField({ label, value, onChange, options, size = 'md', error, required, bare, id: givenId, ...rest }: SelectFieldProps) {
  const auto = useId()
  const id = givenId ?? auto
  return (
    <Wrap id={id} label={label} required={required} error={error} size={size}>
      <select id={id} className={[styles.control, bare && styles.bare].filter(Boolean).join(' ')} value={value} required={required} onChange={(e) => onChange(e.target.value)} {...rest}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Wrap>
  )
}

/* ── Checkbox / Switch ───────────────────────────────────────────── */

export interface ToggleFieldProps {
  label?: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  /* Accessible name when there is no visible label. */
  'aria-label'?: string
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

export function CheckboxField({ label, checked, onChange, disabled, ...rest }: ToggleFieldProps) {
  return (
    <label className={styles.check}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} {...rest} />
      <span className={styles.box}>{checked && <CheckIcon />}</span>
      {label}
    </label>
  )
}

export function SwitchField({ label, checked, onChange, disabled, ...rest }: ToggleFieldProps) {
  return (
    <label className={styles.check}>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} {...rest} />
      <span className={styles.track} />
      {label}
    </label>
  )
}

/* ── Segmented ───────────────────────────────────────────────────── */

export interface SegmentedProps<T extends string> {
  value: T
  onChange: (value: T) => void
  options: { value: T; label: string }[]
  name?: string
  'aria-label'?: string
}

export function Segmented<T extends string>({ value, onChange, options, name, ...rest }: SegmentedProps<T>) {
  const auto = useId()
  const group = name ?? auto
  return (
    <div className={styles.segmented} role="radiogroup" {...rest}>
      {options.map((o) => (
        <label key={o.value} className={styles.segment}>
          <input type="radio" name={group} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
          {o.label}
        </label>
      ))}
    </div>
  )
}

/* ── File ────────────────────────────────────────────────────────── */

export interface FileFieldProps {
  label?: ReactNode
  accept?: string
  multiple?: boolean
  onChange: (files: File[]) => void
  size?: Size
}

export function FileField({ label, accept, multiple, onChange, size = 'md' }: FileFieldProps) {
  const id = useId()
  return (
    <Wrap id={id} label={label} size={size}>
      <input
        id={id}
        type="file"
        className={styles.file}
        accept={accept}
        multiple={multiple}
        onChange={(e) => { onChange(Array.from(e.target.files ?? [])); e.target.value = '' }}
      />
    </Wrap>
  )
}

/* ── Range ───────────────────────────────────────────────────────── */

export interface RangeFieldProps {
  label: ReactNode
  value: number
  onChange: (value: number) => void
  min: number
  max: number
  step?: number
  /* Shown next to the label, e.g. "100%". */
  display?: ReactNode
}

export function RangeField({ label, value, onChange, min, max, step = 1, display }: RangeFieldProps) {
  const id = useId()
  return (
    <div className={styles.field}>
      <div className={styles.rangeHead}>
        <label htmlFor={id}>{label}</label>
        <span>{display ?? value}</span>
      </div>
      <input id={id} type="range" className={styles.range} min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  )
}
